import { create } from 'zustand';
import {
  type SymbolBookState,
  type SymbolBookRuntime,
  type BookLevel,
  type BookSnapshot,
  createEmptyBookState,
  applySnapshot,
  applyDeltaToState,
  mergeHistory,
  scheduleRenderVersion,
  cancelRuntime,
} from './bookStoreInternal';

export type { BookLevel, BookSnapshot, SymbolBookState } from './bookStoreInternal';

/**
 * NEW-6: module-level frozen empty refs. Every `bySymbol[symbol]?.x ?? EMPTY_X`
 * selector across the SR / depth / liquidity / bookmap consumers MUST use
 * these shared references. Returning a fresh `[]`/`new Map()` literal from a
 * Zustand selector re-renders on every store tick (the web-ui stable-selector
 * rule — same discipline as `EMPTY_TRADES`). Do NOT write `?? []` / `?? {}`
 * inside a `useBookStore(...)` selector; use these.
 */
export const EMPTY_LEVELS: readonly BookLevel[] = Object.freeze([]);
export const EMPTY_AGES: ReadonlyMap<number, number> = Object.freeze(
  new Map<number, number>(),
) as ReadonlyMap<number, number>;

type BookStoreState = {
  /** symbol (raw code, e.g. 'XXBTZUSD') → per-symbol book state */
  bySymbol: Record<string, SymbolBookState>;

  setSnapshot: (symbol: string, bids: BookLevel[], asks: BookLevel[]) => void;
  applyDelta: (symbol: string, bids: BookLevel[], asks: BookLevel[]) => void;
  /** Prepend historical snapshots (from REST), deduping by timestamp. */
  loadHistory: (symbol: string, snapshots: BookSnapshot[]) => void;
  clear: (symbol: string) => void;
};

// Per-symbol runtime (rAF/timeout ids, lastSnapshotTs, pending-removal maps).
// Lives outside Zustand state because it's imperative scheduling, not render
// data — identical to how the old singleton kept these at module scope, only
// re-scoped per symbol so two symbols never share a snapshot clock or rAF id.
const runtimes = new Map<string, SymbolBookRuntime>();

function rt(symbol: string): SymbolBookRuntime {
  let r = runtimes.get(symbol);
  if (!r) {
    r = {
      lastSnapshotTs: 0,
      pendingBid: new Map(),
      pendingAsk: new Map(),
      renderFramePending: false,
      renderFrameId: null,
      renderTimeoutId: null,
    };
    runtimes.set(symbol, r);
  }
  return r;
}

export const useBookStore = create<BookStoreState>()((set, get) => ({
  bySymbol: {},

  setSnapshot: (symbol, bids, asks) => {
    set((s) => {
      const prev = s.bySymbol[symbol] ?? createEmptyBookState();
      const next = applySnapshot(prev, bids, asks, rt(symbol));
      return { bySymbol: { ...s.bySymbol, [symbol]: next } };
    });
    scheduleRenderVersion(symbol, rt(symbol), set, get);
  },

  applyDelta: (symbol, bids, asks) => {
    set((s) => {
      const prev = s.bySymbol[symbol] ?? createEmptyBookState();
      const next = applyDeltaToState(prev, bids, asks, rt(symbol));
      return { bySymbol: { ...s.bySymbol, [symbol]: next } };
    });
    scheduleRenderVersion(symbol, rt(symbol), set, get);
  },

  loadHistory: (symbol, snapshots) =>
    set((s) => {
      const prev = s.bySymbol[symbol] ?? createEmptyBookState();
      return {
        bySymbol: { ...s.bySymbol, [symbol]: mergeHistory(prev, snapshots) },
      };
    }),

  clear: (symbol) => {
    cancelRuntime(symbol, runtimes);
    set((s) => {
      const { [symbol]: _gone, ...rest } = s.bySymbol;
      return { bySymbol: rest };
    });
  },
}));

const EMPTY_BOOK = {
  bids: EMPTY_LEVELS as BookLevel[],
  asks: EMPTY_LEVELS as BookLevel[],
  renderVersion: 0,
};

/**
 * Per-symbol render snapshot. Subscribes to this symbol's rAF-coalesced
 * `renderVersion` only, then reads the raw arrays via `getState()` — the same
 * two-step pattern (coalesced counter + getState read) the old singleton used,
 * so OrderBook/DepthChart render bodies are untouched. Returns a stable empty
 * default for not-yet-seeded symbols so panels never crash.
 */
export function useBookRenderSnapshot(symbol: string): {
  bids: BookLevel[];
  asks: BookLevel[];
  renderVersion: number;
} {
  const renderVersion = useBookStore(
    (s) => s.bySymbol[symbol]?.renderVersion ?? 0,
  );
  const entry = useBookStore.getState().bySymbol[symbol];
  if (!entry) return EMPTY_BOOK;
  return { bids: entry.bids, asks: entry.asks, renderVersion };
}
